/**
 * Maps a workspace promotion list row → Gov Promotion inbox event (CT-5095).
 *
 * TEMPORARY: fed by POST `/v1/workspace/promotion/list` until the My Work promotion
 * inbox endpoint replaces the workspace fan-out (CT-4867).
 *
 * Field names mirror `PromotionListItem` in sandbox.yaml and the beta mapper
 * (aims-os-governance `mapApiPromotion`).
 */

import { EVENT_TYPE, CATEGORY } from '../constants/eventTypes.js';
import { SEVERITY } from '../constants/severities.js';
import { DUE_BUCKET } from '../constants/filterOptions.js';
import { riskToSeverity, toWorkQueueStatus } from './mapSourceToEvent.js';
import { buildTaskOwner } from './taskOwner.js';

const NO_DUE = { dueLabel: null, dueBucket: DUE_BUCKET.NO_DATE, dueAt: null, overdue: false };

/** Default pipeline shown in the Gov Promotion prototype when GS sends none. */
export const GOV_PROMOTION_PIPELINE = [
  'Indexing',
  'Claim Detection',
  'Conflict Check',
  'KCON Routing',
];

/**
 * Builds the SITUATION line from claim/conflict counts — mirrors EVT-001 copy.
 * @param {number} claimCount
 * @param {number} conflictCount
 * @param {string|null} description - promotion.description when present
 */
export function buildGovPromotionSituation(claimCount, conflictCount, description) {
  if (description?.trim()) return description.trim();
  const total = claimCount ?? 0;
  const conflicts = conflictCount ?? 0;
  if (conflicts > 0) {
    return `${total} claims extracted, ${conflicts} knowledge conflicts detected. Will block downstream agents until resolved.`;
  }
  if (total > 0) return `${total} claims extracted.`;
  return 'Promotion package awaiting review.';
}

/**
 * @param {Object} promotion - PromotionListItem from POST promotion/list
 * @param {'my_work'|'my_team'} scope
 * @returns {Object|null} raw event for {@link mapApiEventToUi}
 */
export function mapPromotionToEvent(promotion = {}, scope) {
  const promotionUuid = promotion.promotionUuid ?? promotion.promotion_uuid ?? null;
  if (!promotionUuid) return null;

  const claimCount = promotion.claimCount ?? promotion.claim_count ?? 0;
  const title = promotion.title?.trim() || 'Promotion package';
  const code = promotion.code ?? null;
  const sandboxName = promotion.sandboxName ?? promotion.sandbox_name ?? null;
  const kbName = promotion.knowledgebaseName ?? promotion.knowledgebase_name ?? null;
  const ownerUuid = promotion.ownerUuid ?? promotion.owner_uuid ?? null;
  const ownerName = promotion.ownerName ?? promotion.owner_name ?? null;

  return {
    id: promotionUuid,
    scope,
    type: EVENT_TYPE.GOV_PROMOTION,
    category: CATEGORY.GOV,
    severity: riskToSeverity(promotion.riskLevel ?? promotion.risk_level) || SEVERITY.ACTION,
    title,
    description: buildGovPromotionSituation(claimCount, 0, promotion.description),
    refId: code ?? promotionUuid.slice(0, 8).toUpperCase(),
    refLabel: sandboxName ?? 'Gov Promotion',
    actionLabel: 'Approve',
    studio: sandboxName ?? 'governance',
    status: toWorkQueueStatus(promotion.status ?? 'draft'),
    // Promotions are the one governance source that ships a resolved owner:
    // `promotion/list` returns ownerUuid + ownerName (verified live — all 20 draft
    // packages carry one, across 5 distinct people). The name comes with the row,
    // so no directory lookup is needed; `buildTaskOwner` only computes `is_mine`
    // against the viewer uuid and `mapApiEventToUi` normalizes the rest.
    //
    // This drives the owner chip. It does NOT expose the team actions here:
    // promotions are fetched for `my_work` only and those actions are gated on
    // `my_team` — and there is no promotion reassign endpoint, so if that ever
    // changes, Reassign correctly surfaces the unsupported error.
    owner: buildTaskOwner(ownerUuid, ownerName ? { nameById: { [ownerUuid]: ownerName } } : {}),
    ...NO_DUE,
    isCustomer: false,
    missionCritical: Array.isArray(promotion.tags) && promotion.tags.includes('mission_critical'),
    commentsCount: 0,
    situation: buildGovPromotionSituation(claimCount, 0, promotion.description),
    pipeline: GOV_PROMOTION_PIPELINE,
    destination: kbName ?? 'Truth Plane',
    detail: {
      source: EVENT_TYPE.GOV_PROMOTION,
      promotionUuid,
      sandboxUuid: promotion.sandboxUuid ?? promotion.sandbox_uuid ?? null,
      sandboxName,
      knowledgebaseUuid: promotion.knowledgebaseUuid ?? promotion.knowledgebase_uuid ?? null,
      knowledgebaseName: kbName,
      claimCount,
      code,
      ownerUuid,
      ownerName,
      raw: promotion,
    },
  };
}
